import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CHART_COLORS, CHART_LABELS } from './chartConstants';
import './CustomersChart.css';

const ChartBar = ({ width, color, label }) => (
    <div
        className="chart-bar"
        style={{
            width: `${width}%`,
            backgroundColor: color
        }}
        role="img"
        aria-label={`${label}: ${width.toFixed(1)}%`}
    />
);

const LegendItem = ({ color, title, count, percentage }) => (
    <div className="legend-item">
        <div
            className="legend-color"
            style={{ backgroundColor: color }}
            role="img"
            aria-label={`Color indicator for ${title}`}
        />
        <span className="legend-text">
            {title}
            <span className="legend-value">
                - {count} ({percentage.toFixed(1)}%)
            </span>
        </span>
    </div>
);

const CustomersChart = ({ data }) => {
    const chartData = useMemo(() => {
        if (!data?.length) return null;

        // Count unique customers by region
        const customersByRegion = data.reduce((acc, curr) => {
            if (curr.Region === 'US') {
                acc.US.add(curr.CustomerName);
            } else {
                acc.International.add(curr.CustomerName);
            }
            return acc;
        }, {
            US: new Set(),
            International: new Set()
        });

        // Calculate totals
        const usCust = customersByRegion.US.size;
        const intlCust = customersByRegion.International.size;
        const totalCustomers = usCust + intlCust;

        return [
            {
                title: CHART_LABELS.US,
                count: usCust,
                color: CHART_COLORS.US,
                percentage: totalCustomers > 0 ? (usCust / totalCustomers) * 100 : 0
            },
            {
                title: CHART_LABELS.International,
                count: intlCust,
                color: CHART_COLORS.International,
                percentage: totalCustomers > 0 ? (intlCust / totalCustomers) * 100 : 0
            }
        ];
    }, [data]);

    if (!chartData) {
        return (
            <div className="customers-chart empty-state">
                No customer data available
            </div>
        );
    }

    const totalCustomers = chartData.reduce((sum, item) => sum + item.count, 0);

    return (
        <div className="customers-chart">
            <div className="total-customers">
                Total Customers: {totalCustomers}
            </div>

            <div className="chart-bars">
                {chartData.map((item) => (
                    <ChartBar
                        key={item.title}
                        width={item.percentage}
                        color={item.color}
                        label={item.title}
                    />
                ))}
            </div>

            <div className="legend-container">
                {chartData.map((item) => (
                    <LegendItem
                        key={item.title}
                        color={item.color}
                        title={item.title}
                        count={item.count}
                        percentage={item.percentage}
                    />
                ))}
            </div>
        </div>
    );
};

CustomersChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            CustomerName: PropTypes.string,
            Region: PropTypes.string,
            US_cust: PropTypes.number,
            Intl_cust: PropTypes.number,
            TotalCustomer: PropTypes.number,
            TopBuyerShare: PropTypes.number
        })
    ).isRequired
};

ChartBar.propTypes = {
    width: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
};

LegendItem.propTypes = {
    color: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    percentage: PropTypes.number.isRequired
};

export default React.memo(CustomersChart);